import React, { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Card, CardContent, CardMedia, CardActions, Box, Typography, Chip,
  IconButton, Tooltip, Dialog, DialogContent,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import PushPinIcon from '@mui/icons-material/PushPin';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CloseIcon from '@mui/icons-material/Close';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { IListningarEntry, IListningarReactionSummary, IListningarCategory } from './types';
import { getCategoryById, getCategoryByIdFromList } from './categories';
import { toggleReaction } from './listningarApi';
import { API_BASE_URL, BACKEND_ORIGIN } from '../../config';

interface ListningarEntryCardProps {
  entry: IListningarEntry;
  canEdit: boolean;
  canDelete: boolean;
  canFeature: boolean;
  isNew?: boolean;
  currentUserId: string;
  onEdit?: (entry: IListningarEntry) => void;
  onDelete?: (entry: IListningarEntry) => void;
  onToggleHighlight?: (entry: IListningarEntry) => void;
  onTogglePin?: (entry: IListningarEntry) => void;
  variant?: 'normal' | 'highlight';
  categories?: IListningarCategory[];
  cardSize?: 'landscape' | 'square' | 'portrait';
}

function buildReactionSummary(reactions: any[], currentUserId: string): IListningarReactionSummary {
  const summary: IListningarReactionSummary = {};
  for (const r of reactions) {
    const userObj = typeof r.userId === 'object' ? r.userId : null;
    const uid = userObj?._id?.toString?.() || userObj?.toString?.() || r.userId?.toString?.() || '';
    if (!summary[r.type]) {
      summary[r.type] = { count: 0, hasReacted: false, users: [] };
    }
    summary[r.type].count++;
    if (userObj?.firstName || userObj?.lastName) {
      summary[r.type].users!.push(`${userObj.firstName || ''} ${userObj.lastName || ''}`.trim());
    }
    if (uid === currentUserId) {
      summary[r.type].hasReacted = true;
    }
  }
  return summary;
}

const ListningarEntryCard: React.FC<ListningarEntryCardProps> = ({
  entry,
  canEdit,
  canDelete,
  canFeature,
  isNew = false,
  currentUserId,
  onEdit,
  onDelete,
  onToggleHighlight,
  onTogglePin,
  variant = 'normal',
  categories,
  cardSize = 'landscape',
}) => {
  const [reactionSummary, setReactionSummary] = useState<IListningarReactionSummary>(
    buildReactionSummary(entry.reactions || [], currentUserId)
  );
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const category = categories ? getCategoryByIdFromList(categories, entry.category) : getCategoryById(entry.category);
  const isHighlightVariant = variant === 'highlight';
  const sanitizedDescription = useMemo(
    () => DOMPurify.sanitize(entry.description || '', { USE_PROFILES: { html: true } }),
    [entry.description]
  );

  const isPortrait = cardSize === 'portrait';
  const portraitRatio = '210 / 297';
  const mediaHeight = isPortrait
    ? undefined
    : isHighlightVariant
      ? (cardSize === 'square' ? 280 : 220)
      : (cardSize === 'square' ? 200 : 160);

  const handleReaction = async (type: string) => {
    try {
      const result = await toggleReaction(entry._id, type);
      setReactionSummary(result.summary);
    } catch (err) {
      console.error('Failed to toggle reaction:', err);
    }
  };

  const mediaUrl = (url: string) => {
    if (url.startsWith('http')) return url;
    return `${API_BASE_URL}${url}`;
  };

  const firstImage = entry.media?.find(m => m.type === 'image');
  const firstVideo = entry.media?.find(m => m.type === 'video');
  const firstPdf = entry.media?.find(m => m.type === 'pdf');
  const hasMultipleMedia = (entry.media?.length || 0) > 1;
  const currentMediaIsPdf = entry.media?.[lightboxIndex]?.type === 'pdf';

  const dateStr = new Date(entry.createdAt).toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'short',
  });

  return (
    <>
      <Card
        elevation={isHighlightVariant ? 3 : 1}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          transition: 'all 0.25s ease',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          border: isHighlightVariant ? `2px solid ${category?.color || '#072f53'}` : '1px solid',
          borderColor: isHighlightVariant ? (category?.color || '#072f53') : 'divider',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
          ...(isHighlightVariant && {
            width: '100%',
          }),
        }}
      >
        {isNew && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
              bgcolor: '#FF5722',
              color: '#fff',
              px: 1,
              py: 0.3,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <FiberNewIcon sx={{ fontSize: 14 }} />
            NY
          </Box>
        )}

        {entry.isPinned && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
            }}
          >
            <Tooltip title="Fäst inlägg">
              <PushPinIcon sx={{ color: '#FF9800', fontSize: 20, transform: 'rotate(45deg)' }} />
            </Tooltip>
          </Box>
        )}

        {firstImage && (
          <Box
            sx={{
              position: 'relative',
              cursor: 'pointer',
              '&:hover .media-overlay': { opacity: 1 },
            }}
            onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
          >
            <CardMedia
              component="img"
              {...(mediaHeight ? { height: mediaHeight } : {})}
              image={mediaUrl(firstImage.url)}
              alt={entry.title}
              sx={{
                objectFit: 'cover',
                objectPosition: firstImage.objectPosition || 'center center',
                ...(isPortrait ? { aspectRatio: portraitRatio, width: '100%', height: 'auto' } : {}),
              }}
            />
            {hasMultipleMedia && (
              <Box
                className="media-overlay"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  bgcolor: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                +{entry.media.length - 1} till
              </Box>
            )}
            {category && (
              <Chip
                label={category.label}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: category.color,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 22,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }}
              />
            )}
          </Box>
        )}

        {!firstImage && firstVideo && (
          <Box
            sx={{
              ...(isPortrait ? { aspectRatio: portraitRatio } : { height: mediaHeight }),
              bgcolor: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
            }}
            onClick={() => { setLightboxIndex(entry.media.findIndex(m => m === firstVideo)); setLightboxOpen(true); }}
          >
            {firstVideo.videoPreviewUrl ? (
              <video
                src={mediaUrl(firstVideo.videoPreviewUrl)}
                autoPlay
                loop
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: firstVideo.objectPosition || 'center center' }}
              />
            ) : firstVideo.thumbnailUrl ? (
              <img
                src={mediaUrl(firstVideo.thumbnailUrl)}
                alt={entry.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: firstVideo.objectPosition || 'center center' }}
              />
            ) : (
              <PlayCircleIcon sx={{ fontSize: 64, color: '#fff', opacity: 0.8 }} />
            )}
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}>
              <PlayCircleIcon sx={{ fontSize: 48, color: '#fff', opacity: 0.85, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
            </Box>
            {category && (
              <Chip
                label={category.label}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: category.color,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 22,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }}
              />
            )}
          </Box>
        )}

        {!firstImage && !firstVideo && firstPdf && (
          <Box
            sx={{
              ...(isPortrait ? { aspectRatio: portraitRatio } : { height: mediaHeight }),
              bgcolor: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
            }}
            onClick={() => { setLightboxIndex(entry.media.findIndex(m => m === firstPdf)); setLightboxOpen(true); }}
          >
            {firstPdf.thumbnailUrl ? (
              <>
                <img
                  src={mediaUrl(firstPdf.thumbnailUrl)}
                  alt={firstPdf.originalName || 'PDF'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: firstPdf.objectPosition || 'center center' }}
                />
                <Box sx={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', px: 1, py: 0.3, borderRadius: 1 }}>
                  <PictureAsPdfIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>PDF</Typography>
                </Box>
              </>
            ) : (
              <>
                <PictureAsPdfIcon sx={{ fontSize: 56, color: '#E53935' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, px: 1, textAlign: 'center' }}>
                  {firstPdf.originalName || 'PDF-dokument'}
                </Typography>
              </>
            )}
            {hasMultipleMedia && (
              <Box sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', px: 1, py: 0.3, borderRadius: 1, fontSize: '0.7rem', fontWeight: 600 }}>
                +{entry.media.length - 1} till
              </Box>
            )}
            {category && (
              <Chip
                label={category.label}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: category.color,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 22,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }}
              />
            )}
          </Box>
        )}

        <CardContent sx={{
          pb: '0px !important',
          pt: 1.5,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography
              variant={isHighlightVariant ? 'h6' : 'subtitle1'}
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={entry.title}
            >
              {entry.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mt: 0.3 }}>
              {entry.isHighlight && (
                <Tooltip title="Veckans highlight">
                  <StarIcon sx={{ color: '#FFD700', fontSize: 16 }} />
                </Tooltip>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {dateStr}
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            component="div"
            sx={{
              fontSize: '0.8rem',
              lineHeight: 1.4,
              height: `${2 * 1.4}em`,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              '& p': { margin: 0 },
              '& img': { display: 'none' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedDescription || '&nbsp;' }}
          />

          <Box sx={{ flexGrow: 1 }} />
        </CardContent>

        <CardActions sx={{ px: 2, pb: 1, pt: 0.5, justifyContent: 'space-between', mt: 'auto' }}>
          {(() => {
            const data = reactionSummary.heart;
            const hasReacted = data?.hasReacted || false;
            const count = data?.count || 0;
            const userNames = data?.users || [];
            const tooltipText = userNames.length > 0
              ? userNames.join(', ')
              : 'Gilla';

            return (
              <Tooltip title={tooltipText} arrow placement="top">
                <Box
                  onClick={() => handleReaction('heart')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'transform 0.15s ease',
                    '&:hover': { transform: 'scale(1.15)' },
                    '&:active': { transform: 'scale(0.9)' },
                  }}
                >
                  <Box sx={{
                    fontSize: '1.3rem',
                    lineHeight: 1,
                    filter: hasReacted ? 'none' : 'grayscale(1) opacity(0.4)',
                    transition: 'filter 0.2s ease',
                  }}>
                    ❤️
                  </Box>
                  {count > 0 && (
                    <Typography variant="caption" sx={{ fontWeight: 600, color: hasReacted ? 'error.main' : 'text.secondary' }}>
                      {count}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })()}

          {(canFeature || canEdit || canDelete) && (
            <Box sx={{ display: 'flex', gap: 0.3 }}>
              {canFeature && (
                <Tooltip title={entry.isHighlight ? 'Ta bort highlight' : 'Markera som highlight'}>
                  <IconButton size="small" onClick={() => onToggleHighlight?.(entry)}>
                    <StarIcon sx={{ fontSize: 18, color: entry.isHighlight ? '#FFD700' : 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
              {canFeature && (
                <Tooltip title={entry.isPinned ? 'Ta bort nål' : 'Fäst inlägg'}>
                  <IconButton size="small" onClick={() => onTogglePin?.(entry)}>
                    <PushPinIcon sx={{ fontSize: 18, color: entry.isPinned ? '#FF9800' : 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
              {canEdit && (
                <Tooltip title="Redigera">
                  <IconButton size="small" onClick={() => onEdit?.(entry)}>
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Ta bort">
                  <IconButton size="small" onClick={() => onDelete?.(entry)} color="error">
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </CardActions>
      </Card>

      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={currentMediaIsPdf ? false : 'lg'}
        fullWidth
        fullScreen={currentMediaIsPdf}
        sx={{ zIndex: 20000 }}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            boxShadow: 'none',
            borderRadius: currentMediaIsPdf ? 0 : 2,
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: currentMediaIsPdf ? '100vh' : 400 }}>
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 10 }}
          >
            <CloseIcon />
          </IconButton>

          {entry.media?.length > 1 && (
            <>
              <IconButton
                onClick={() => setLightboxIndex(prev => (prev - 1 + entry.media.length) % entry.media.length)}
                sx={{
                  position: 'absolute', left: 16, color: '#fff', zIndex: 10,
                  bgcolor: 'rgba(0,0,0,0.55)', width: 48, height: 48,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                }}
              >
                <NavigateBeforeIcon sx={{ fontSize: 32 }} />
              </IconButton>
              <IconButton
                onClick={() => setLightboxIndex(prev => (prev + 1) % entry.media.length)}
                sx={{
                  position: 'absolute', right: 16, color: '#fff', zIndex: 10,
                  bgcolor: 'rgba(0,0,0,0.55)', width: 48, height: 48,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                }}
              >
                <NavigateNextIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </>
          )}

          {entry.media?.[lightboxIndex] && (
            entry.media[lightboxIndex].type === 'video' ? (
              <video
                src={mediaUrl(entry.media[lightboxIndex].url)}
                controls
                autoPlay
                style={{ maxWidth: '100%', maxHeight: '80vh' }}
              />
            ) : entry.media[lightboxIndex].type === 'pdf' ? (
              <iframe
                src={`${BACKEND_ORIGIN}${entry.media[lightboxIndex].url}`}
                title={entry.media[lightboxIndex].originalName || 'PDF'}
                style={{
                  width: '100%',
                  height: '100vh',
                  border: 'none',
                }}
              />
            ) : (
              <img
                src={mediaUrl(entry.media[lightboxIndex].url)}
                alt={entry.media[lightboxIndex].caption || entry.title}
                style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            )
          )}

          {entry.media?.length > 1 && (
            <Box sx={{ position: 'absolute', bottom: 16, display: 'flex', gap: 1 }}>
              {entry.media.map((m, i) => (
                <Box
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: i === lightboxIndex ? '2px solid #fff' : '2px solid transparent',
                    cursor: 'pointer',
                    opacity: i === lightboxIndex ? 1 : 0.6,
                    transition: 'opacity 0.15s',
                    '&:hover': { opacity: 1 },
                  }}
                >
                  {m.type === 'image' ? (
                    <img src={mediaUrl(m.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : m.type === 'pdf' ? (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PictureAsPdfIcon sx={{ color: '#E53935', fontSize: 20 }} />
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PlayCircleIcon sx={{ color: '#fff', fontSize: 20 }} />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ListningarEntryCard;
